"use client"

import type React from "react"
import { useState, useEffect, useRef } from "react"
import { BillingsList, type BillingFilters } from "./list"
import { useDebounce } from "../../hooks/useDebounce"
import { EditBillingOffcanvas } from "./edit"
import toast from "react-hot-toast"
import axiosInstance from "../../libs/axios"
import { ConfirmModal } from "../../components/ConfirmModal"
import type { SelectOption } from "../../utils/select-with-search"

export enum BillingStatus {
  PENDING = "pending",
  WCC_RAISED = "wcc_raised",
  WCC_APPROVED = "wcc_approved",
  INVOICE_RAISED = "invoice_raised",
  INVOICE_SUBMITTED = "invoice_submitted",
  PAID = "paid",
}

export enum PoStatus {
  PENDING = "pending",
  RECEIVED = "received",
  REJECTED = "rejected",
}

export enum WccStatus {
  PENDING = "pending",
  APPROVED = "approved",
  REJECTED = "rejected",
}

export enum InvoiceStatus {
  DRAFT = "draft",
  SUBMITTED = "submitted",
  APPROVED = "approved",
  PAID = "paid",
  CANCELLED = "cancelled",
}

export interface StateCircle {
  id: number
  circle_name: string
  state_name: string
}

export interface Activity {
  id: number
  activity_name: string
}

export interface Project {
  id: number
  project_name: string
  activity: Activity
}

export interface ProjectSite {
  id: number
  site_id: string
  site_name: string
  project: Project
  state_circle: StateCircle
}

export interface Billing {
  id: number
  project_site_id: number
  tc_po_number: string
  tc_po_date: string
  is_po_given_at_allocation: boolean
  billing_status: string // 'pending' | 'wcc_raised' | 'wcc_approved' | 'invoice_raised' | 'invoice_submitted' | 'paid'
  activity_amount: string
  billed_site_id: string
  billed_ref_id: string
  billed_po_number: string
  billed_po_date: string
  wcc_date: string
  wcc_number: string
  wcc_status: string // 'pending' | 'approved' | 'rejected'
  receipt_number: string
  invoice_number: string
  invoice_date: string
  invoice_amount: string
  invoice_status: string // 'draft' | 'submitted' | 'approved' | 'paid' | 'cancelled'
  manual_site_name: string | null
  manual_site_reference: string | null
  manual_client_allocation_date: string | null
  manual_circle_name: string | null
  manual_project_name: string | null
  manual_activity_name: string | null
  created_by: number
  updated_by: number
  created_at: string
  updated_at: string
  project_id: number | null
  purchase_order_id: number | null
  project_site: ProjectSite
}

interface Pagination {
  offset: number
  limit: number
  page: number
  total_pages: number
  total_count: number
  has_next_page: boolean
  has_prev_page: boolean
  next_page: number | null
  prev_page: number | null
}

export const BillingsManagement: React.FC = () => {
  const [billings, setBillings] = useState<Billing[]>([])
  const [pagination, setPagination] = useState<Pagination>({
    offset: 0,
    limit: 10,
    page: 1,
    total_pages: 0,
    total_count: 0,
    has_next_page: false,
    has_prev_page: false,
    next_page: null,
    prev_page: null,
  })
  const [loading, setLoading] = useState(false)
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedBilling, setSelectedBilling] = useState<Billing | null>(null)
  const [confirmDeleteBilling, setConfirmDeleteBilling] = useState<Billing | null>(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState("")
  const [projectSiteFilter, setProjectSiteFilter] = useState("")

  // Debounced search — API is only called 400 ms after the user stops typing
  const debouncedSearch = useDebounce(searchTerm, 400)
  const [sortConfig, setSortConfig] = useState<{ field: string; direction: "ASC" | "DESC" }>({
    field: "created_at",
    direction: "DESC",
  })
  const [advancedFilters, setAdvancedFilters] = useState<BillingFilters>({})
  const [projectOptions, setProjectOptions] = useState<SelectOption[]>([])
  const [activityOptions, setActivityOptions] = useState<SelectOption[]>([])

  // Fetch billings
  const fetchBillings = async (
    page = 1,
    limitValue?: number,
    sort = sortConfig,
    filtersParam: BillingFilters = advancedFilters,
  ) => {
    setLoading(true)
    try {
      const effectiveLimit = limitValue ?? pagination.limit ?? 10

      const params: any = {
        page,
        limit: effectiveLimit,
      }

      if (debouncedSearch) params.search = debouncedSearch
      if (statusFilter) params.billing_status = statusFilter
      if (projectSiteFilter) params.project_site_id = projectSiteFilter
      if (filtersParam) {
        Object.entries(filtersParam).forEach(([key, value]) => {
          if (!value) return
          if (key === "project_label" || key === "activity_label") return
          params[key] = value
        })
      }
      if (sort?.field) {
        params.sort_by = sort.field
        params.sort_order = sort.direction
      }

      const response = await axiosInstance.get("/billing", { params })
      setBillings(response.data.data.billings)
      setPagination(response.data.data.pagination)
    } catch (error: any) {
      console.log(error.response?.data?.message || "Failed to fetch billings")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchBillings()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, statusFilter, projectSiteFilter])

  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const [projectsResponse, activitiesResponse] = await Promise.all([
          axiosInstance.get("/projects/list", {
            params: { limit: 100, status: "ACTIVE" },
          }),
          axiosInstance.get("/activities/list", {
            params: { limit: 100, status: "ACTIVE" },
          }),
        ])

        if (projectsResponse.data?.success) {
          const projectData = projectsResponse.data?.data?.projects || []
          const options: SelectOption[] = projectData.map((project: any) => ({
            id: project.id,
            value: project.id,
            label: project.project_name || `Project #${project.id}`,
          }))
          setProjectOptions(options)
        }

        if (activitiesResponse.data?.success) {
          const activityData = activitiesResponse.data?.data?.activities || []
          const options: SelectOption[] = activityData.map((activity: any) => ({
            id: activity.id,
            value: activity.id,
            label: activity.activity_name || `Activity #${activity.id}`,
          }))
          setActivityOptions(options)
        }
      } catch (error) {
        console.error("Error fetching billing filter options:", error)
      }
    }

    fetchFilterOptions()
  }, [])

  // Handle search
  const handleSearch = (search: string) => {
    setSearchTerm(search)
  }

  // Handle status filter
  const handleStatusFilter = (status: string) => {
    setStatusFilter(status)
  }

  // Handle project site filter
  const handleProjectSiteFilter = (projectSiteId: string) => {
    setProjectSiteFilter(projectSiteId)
  }

  // Handle page change
  const handlePageChange = (page: number) => {
    fetchBillings(page, undefined, sortConfig)
  }

  // Handle limit change
  const handleLimitChange = (limit: number) => {
    setPagination((prev) => ({ ...prev, limit, page: 1 }))
    fetchBillings(1, limit, sortConfig)
  }

  const handleSort = (field: string, direction: "ASC" | "DESC") => {
    const sort = { field, direction }
    setSortConfig(sort)
    fetchBillings(pagination.page, undefined, sort)
  }

  const handleAdvancedFilter = (updatedFilters: BillingFilters) => {
    setAdvancedFilters(updatedFilters)
    fetchBillings(1, undefined, sortConfig, updatedFilters)
  }

  // Handle add billing
  const handleAddBilling = () => {
    setShowAddModal(true)
  }

  // Handle submit new billing
  const handleSubmitNewBilling = async (data: any) => {
    try {
      await axiosInstance.post("/billing", data)
      toast.success("Billing created successfully")
      setShowAddModal(false)
      fetchBillings(pagination.page)
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to create billing")
      throw error
    }
  }

  // Handle edit billing
  const handleEditBilling = (billing: Billing) => {
    setSelectedBilling(billing)
    setShowEditModal(true)
  }

  // Handle submit edit billing
  const handleSubmitEditBilling = async (data: any) => {
    if (!selectedBilling) return

    try {
      await axiosInstance.patch(`/billing/${selectedBilling.id}`, data)
      toast.success("Billing updated successfully")
      setShowEditModal(false)
      setSelectedBilling(null)
      fetchBillings(pagination.page)
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to update billing")
      throw error
    }
  }

  // Handle delete billing
  const handleDeleteBilling = (billing: Billing) => {
    setConfirmDeleteBilling(billing)
  }

  const executeDeleteBilling = async () => {
    if (!confirmDeleteBilling) return
    try {
      await axiosInstance.delete(`/billing/${confirmDeleteBilling.id}`)
      toast.success("Billing deleted successfully")
      fetchBillings(pagination.page)
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to delete billing")
    } finally {
      setConfirmDeleteBilling(null)
    }
  }

  // Handle update billing status
  const handleUpdateBillingStatus = async (billing: Billing, newStatus: BillingStatus) => {
    try {
      await axiosInstance.patch(`/billing/${billing.id}/status`, {
        billing_status: newStatus,
      })
      toast.success("Billing status updated successfully")
      fetchBillings(pagination.page)
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to update billing status")
    }
  }

  // Handle update PO status
  const handleUpdatePoStatus = async (billing: Billing, newStatus: PoStatus) => {
    try {
      await axiosInstance.patch(`/billing/${billing.id}/po-status`, {
        billed_po_status: newStatus,
      })
      toast.success("PO status updated successfully")
      fetchBillings(pagination.page)
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to update PO status")
    }
  }

  // Handle update WCC status
  const handleUpdateWccStatus = async (billing: Billing, newStatus: WccStatus) => {
    try {
      await axiosInstance.patch(`/billing/${billing.id}/wcc-status`, {
        wcc_status: newStatus,
      })
      toast.success("WCC status updated successfully")
      fetchBillings(pagination.page)
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to update WCC status")
    }
  }

  return (
    <div className="container mx-auto">
      <BillingsList
        billings={billings}
        pagination={pagination}
        onSearch={handleSearch}
        onStatusFilter={handleStatusFilter}
        onProjectSiteFilter={handleProjectSiteFilter}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
        onAddBilling={handleAddBilling}
        onEditBilling={handleEditBilling}
        onDeleteBilling={handleDeleteBilling}
        onUpdateBillingStatus={handleUpdateBillingStatus}
        onUpdatePoStatus={handleUpdatePoStatus}
        onUpdateWccStatus={handleUpdateWccStatus}
        onAdvancedFilter={handleAdvancedFilter}
        loading={loading}
        sortConfig={sortConfig}
        onSort={handleSort}
        projectOptions={projectOptions}
        activityOptions={activityOptions}
        appliedFilters={advancedFilters}
        searchValue={searchTerm}
        statusValue={statusFilter}
        projectSiteValue={projectSiteFilter}
      />

      {selectedBilling && (
        <EditBillingOffcanvas
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false)
            setSelectedBilling(null)
          }}
          onSubmit={handleSubmitEditBilling}
          billing={selectedBilling}
        />
      )}

      <ConfirmModal
        isOpen={!!confirmDeleteBilling}
        onClose={() => setConfirmDeleteBilling(null)}
        onConfirm={executeDeleteBilling}
        title="Delete Billing"
        message="Are you sure you want to delete this billing? This action cannot be undone."
        confirmText="Delete"
        variant="danger"
      />
    </div>
  )
}
